import { useContext, useEffect, useState } from "react";

import styled from "styled-components/macro";

import API from "Signups/API";
import { SignupsAppState } from "Signups/state.js";
import { motion } from "framer-motion";

import FnrLoadingBricks from "UI/FnrLoadingBricks2";
import { FnrPopover } from "UI/FnrPopover2";
import { HoverChild, HoverParent } from "UI/HoverChildren";
import { scrollbarDarkThin } from "UI/scrollbarStyles";

export default function AddRespondentsToGroup({
  show = false,
  setShow,
  buttonRef,
  signupListItemID,
}) {
  const [
    {
      currentSignupList: { ID: signupListID },
    },
  ] = useContext(SignupsAppState);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [groups, setGroups] = useState([]);

  const [isAddingToGroup, setIsAddingToGroup] = useState(false);

  const checkPopoverToClose = () => {
    if (isAddingToGroup) return;
    setShow(false);
  };

  useEffect(() => {
    if (show) {
      const req = API.getAvailableGroupsForBulkAction(signupListID);

      setIsLoading(true);
      setFetchError(false);
      req
        .post()
        .then((responseData) => {
          setIsLoading(false);
          setGroups(responseData.availableGroupsForBulkAction);
        })
        .catch((err) => {
          setIsLoading(false);
          setFetchError(true);
        });
    }
  }, [show, signupListID]);

  const handleGroupClick = (groupID) => {
    let req;

    setIsAddingToGroup(groupID);

    if (signupListItemID) {
      req = API.addSignupListItemToGroup({
        signupListID,
        signupListItemID,
        groupID,
      });
    } else {
      req = API.addSignupListToGroup({ signupListID, groupID });
    }

    req
      .post()
      .then(() => {
        window.fnReactBus.checkBackgroundJobs();
        setShow(false);
        setIsAddingToGroup(false);
      })
      .catch(() => {
        alert("There was a problem adding the respondents to the group.");
      });
  };

  const maxHeight = Math.max(
    window.innerHeight -
      buttonRef?.current?.getBoundingClientRect()?.bottom -
      20,
    250
  );

  return (
    <FnrPopover
      show={show}
      buttonRef={buttonRef}
      onShouldClose={checkPopoverToClose}
    >
      <PopoverContent style={{ maxHeight }}>
        <HelpText>
          Select a group to add everyone currently signed up for{" "}
          {signupListItemID ? "this item" : "anything in this signup"} to.
        </HelpText>
        <ListContainer>
          <LoadingHolder>
            <LoadingInside>
              <FnrLoadingBricks loading={isLoading} size={28} />
            </LoadingInside>
          </LoadingHolder>
          {groups.length > 0 &&
            (fetchError ? (
              <Error>
                Oh no! There was a problem loading the list of available groups.
              </Error>
            ) : (
              <HoverParent hoverStyle={{ background: "rgba(255,255,255,.2)" }}>
                <List
                  initial={{ height: 120, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                >
                  {groups.map((group) => (
                    <HoverChild
                      key={group.value}
                      disabled={
                        isAddingToGroup && isAddingToGroup !== group.value
                      }
                      forceHover={isAddingToGroup === group.value}
                    >
                      <GroupLine
                        onClick={
                          isAddingToGroup
                            ? null
                            : () => handleGroupClick(group.value)
                        }
                        className={"indent_" + group.indent}
                        disabled={isAddingToGroup}
                      >
                        <FnrLoadingBricks
                          loading={isAddingToGroup === group.value}
                        >
                          <GroupIcon photoURL={group.photoURL} />
                        </FnrLoadingBricks>
                        <GroupName>{group.name}</GroupName>
                      </GroupLine>
                    </HoverChild>
                  ))}
                </List>
              </HoverParent>
            ))}
        </ListContainer>
      </PopoverContent>
    </FnrPopover>
  );
}

const PopoverContent = styled.div`
  width: 230px;
  overflow: auto;
  ${scrollbarDarkThin}
`;

const LoadingHolder = styled.div`
  position: absolute;
  height: 120px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const LoadingInside = styled.div`
  width: 28px;
  height: 28px;
  margin-left: -28px;
`;

const ListContainer = styled.div`
  min-height: 120px;
`;

const List = styled(motion.div)`
  margin: 0;
  padding: 0;
`;

const Error = styled.div`
  color: #911919;
  font-size: 15px;
  padding: 5px;
`;

const GroupLine = styled.div`
  display: flex;
  align-items: center;
  padding: 6px;
  position: relative;
  z-index: 100;
  cursor: ${({ disabled }) => (disabled ? "wait" : "pointer")};
  &.indent_1 {
    padding-left: 12px;
  }
  &.indent_2 {
    padding-left: 24px;
  }
`;
const GroupIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 24px;
  background: #fff;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  background-image: url(${(props) => props.photoURL});
  border: 2px solid #fff;
  cursor: pointer;
`;
const GroupName = styled.div`
  margin-left: 6px;
`;

const HelpText = styled.div`
  padding: 8px 0;
  margin: 0 8px;
  font-size: 14px;
  line-height: 17px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;
